import React, {useEffect, useRef, useState} from 'react';
import {Autocomplete, TextField, TextFieldProps} from '@mui/material';
import {Loader} from '@googlemaps/js-api-loader';
import {logError} from '../services/logger.service';

const MAX_ADDRESS_SUGGESTIONS = 10;
const SUGGEST_DEBOUNCE_MS = 280;
const BLUR_DISMISS_MS = 180;

export interface ParsedAddressFields {
  street: string;
  city: string;
  state: string;
  postalCode: string;
}

interface AddressOption extends ParsedAddressFields {
  label: string;
  placeId: string;
}

interface AddressAutocompleteFieldProps {
  value: string;
  onChange: (street: string) => void;
  onAddressPicked: (parsed: ParsedAddressFields) => void;
  label?: string;
  sx?: TextFieldProps['sx'];
}

const stripCountry = (value: string): string =>
  value.replace(/, USA$/, '').replace(/, United States$/, '').trim();

/** Last-resort split of "123 Main St, Boise, ID 83702" when components are missing. */
export const parseFormattedAddressLine = (formatted: string): ParsedAddressFields => {
  const cleaned = stripCountry(formatted);
  const parts = cleaned.split(',').map(part => part.trim()).filter(Boolean);
  const street = parts[0] ?? cleaned;
  const city = parts[1] ?? '';
  const region = (parts[2] ?? '').split(/\s+/).filter(Boolean);
  return {
    street,
    city,
    state: region[0] ?? '',
    postalCode: region.slice(1).join(' '),
  };
};

const findComponent = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  ...types: string[]
): google.maps.GeocoderAddressComponent | undefined => {
  if (!components) {
    return undefined;
  }
  for (const type of types) {
    const found = components.find(component => component.types.includes(type));
    if (found) {
      return found;
    }
  }
  return undefined;
};

/**
 * Split a Places result the same way the Android street field does: street
 * in the box, city / state / zip filled beside it. A missing component falls
 * back to the formatted line.
 */
export const parsePlaceResult = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  formattedAddress: string | undefined,
  fallbackLabel: string,
): ParsedAddressFields => {
  const formatted = stripCountry(formattedAddress || fallbackLabel);
  const fromLine = parseFormattedAddressLine(formatted);
  const streetNumber = findComponent(components, 'street_number')?.long_name ?? '';
  const route = findComponent(components, 'route')?.long_name ?? '';
  const street = [streetNumber, route].filter(Boolean).join(' ');
  const city = findComponent(
    components,
    'locality',
    'postal_town',
    'sublocality_level_1',
    'sublocality',
    'administrative_area_level_3',
  )?.long_name ?? '';
  const statePart = findComponent(components, 'administrative_area_level_1');
  const state = (statePart?.short_name || statePart?.long_name || '').trim();
  const postal = findComponent(components, 'postal_code')?.long_name ?? '';
  const suffix = findComponent(components, 'postal_code_suffix')?.long_name;
  const postalCode = postal && suffix ? `${postal}-${suffix}` : postal;
  return {
    street: street || fromLine.street,
    city: city || fromLine.city,
    state: state || fromLine.state,
    postalCode: postalCode || fromLine.postalCode,
  };
};

/**
 * Street-address field with the same Places search the client and estimate
 * address fields use. Suggestions stay visible above a modal (the list is
 * portaled) and are not re-filtered by MUI, which would hide Places hits
 * that don't contain the typed text as a raw substring.
 *
 * A saved street must not query on its own. Search only after the field is
 * focused and the user types.
 */
const AddressAutocompleteField: React.FC<AddressAutocompleteFieldProps> = ({
  value,
  onChange,
  onAddressPicked,
  label = 'Street address',
  sx,
}) => {
  const [suggestions, setSuggestions] = useState<AddressOption[]>([]);
  const [focused, setFocused] = useState(false);
  const [typedSinceFocus, setTypedSinceFocus] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);
  const suppressQuery = useRef(false);
  const queryGen = useRef(0);

  useEffect(() => {
    const googleMapsLoader = new Loader({
      apiKey: 'AIzaSyBLw50vYSozTEviDIpu94K1KuwM4X_hDUM',
      version: 'beta',
    });
    let cancelled = false;
    Promise.all([
      googleMapsLoader.importLibrary('places'),
      googleMapsLoader.importLibrary('maps'),
    ])
      .then(([places, maps]) => {
        if (cancelled) {
          return;
        }
        const placesLibrary = places as google.maps.PlacesLibrary;
        const mapsLibrary = maps as google.maps.MapsLibrary;
        setAutocompleteService(new placesLibrary.AutocompleteService());
        const mapDiv = document.createElement('div');
        const map = new mapsLibrary.Map(mapDiv, {});
        setPlacesService(new placesLibrary.PlacesService(map));
      })
      .catch(error => {
        logError('Error importing google places library', {}, error as Error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (suppressQuery.current) {
      suppressQuery.current = false;
      setSuggestions([]);
      return;
    }
    const autocomplete = autocompleteService;
    const places = placesService;
    const canQuery = focused && typedSinceFocus && value.trim().length >= 2
      && autocomplete != null && places != null;
    if (!canQuery || autocomplete == null || places == null) {
      // Blur first, then a suggestion click: keep the list for a beat so
      // the click still lands instead of the list vanishing under it.
      const timer = window.setTimeout(() => setSuggestions([]), focused ? 0 : BLUR_DISMISS_MS);
      return () => window.clearTimeout(timer);
    }

    const gen = ++queryGen.current;
    const timer = window.setTimeout(() => {
      autocomplete.getPlacePredictions({
        input: value,
        componentRestrictions: {country: 'us'},
        locationBias: 'IP_BIAS',
        region: 'US',
      })
        .then(response => {
          const predictions = response.predictions.slice(0, MAX_ADDRESS_SUGGESTIONS);
          const detailPromises = predictions.map(prediction => new Promise<AddressOption>(resolve => {
            places.getDetails({
              placeId: prediction.place_id,
              fields: ['address_components', 'formatted_address'],
            }, (place, status) => {
              const fallback = stripCountry(prediction.description);
              if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                const parsed = parsePlaceResult(place.address_components, place.formatted_address, fallback);
                resolve({
                  ...parsed,
                  label: stripCountry(place.formatted_address || prediction.description),
                  placeId: prediction.place_id,
                });
              } else {
                resolve({
                  ...parseFormattedAddressLine(fallback),
                  label: fallback,
                  placeId: prediction.place_id,
                });
              }
            });
          }));
          return Promise.all(detailPromises);
        })
        .then(options => {
          if (gen === queryGen.current) {
            setSuggestions(options);
          }
        })
        .catch(error => {
          logError('Error getting address suggestions', {address: value}, error as Error);
          if (gen === queryGen.current) {
            setSuggestions([]);
          }
        });
    }, SUGGEST_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [autocompleteService, focused, placesService, typedSinceFocus, value]);

  const open = suggestions.length > 0;

  return (
    <Autocomplete
      freeSolo
      fullWidth
      open={open}
      options={suggestions}
      filterOptions={options => options}
      inputValue={value}
      getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
      isOptionEqualToValue={(option, selected) => option.placeId === selected.placeId}
      onInputChange={(_, next, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setTypedSinceFocus(true);
          onChange(next);
        }
      }}
      onChange={(_, option) => {
        if (option && typeof option !== 'string') {
          suppressQuery.current = true;
          setTypedSinceFocus(false);
          setSuggestions([]);
          onAddressPicked(option);
        }
      }}
      componentsProps={{
        popper: {style: {zIndex: 1600}},
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.placeId}>
          {option.label}
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          sx={sx}
          inputProps={{
            ...params.inputProps,
            onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
              params.inputProps.onFocus?.(event);
              setFocused(true);
            },
            onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
              params.inputProps.onBlur?.(event);
              setFocused(false);
              setTypedSinceFocus(false);
            },
          }}
        />
      )}
    />
  );
};

export default AddressAutocompleteField;
